import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { usePersonalization } from '../../providers/PersonalizationProvider';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';
import { useEscapeLayer, useSaveShortcut } from '../settings/key-layers';
import {
  MAX_PERSONALIZATION_FILE_BYTES,
  PERSONALIZATION_FILE_DESCRIPTIONS,
  PERSONALIZATION_FILE_LABELS,
  PERSONALIZATION_TABS,
  editorLines,
  markdownLines,
  matchLineEndings,
  personalizationByteLength,
  type EditorLine,
  type PersonalizationFileName,
  type PersonalizationTabId,
  type TokenColor,
} from '../settings/personalization';
import { FieldError, MONO_FONT, Notice, TYPE } from '../settings/primitives';

/** False when the clipboard refused, so "Copied" is never shown for a copy
 *  that did not happen. */
async function copyText(text: string): Promise<boolean> {
  try {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard?.writeText == null) return false;
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * The persona editor: two tabs (Agent, User) over one editing session held in
 * the provider, so drafts survive tab switches and navigation. Edit mode is a
 * transparent textarea over the highlighted read view.
 */
export default function PersonaEditor() {
  const api = usePersonalization();
  const ready = api.status === 'ready';
  const { editing, saving, drafts, dirtyFiles } = api;
  const unreadable = api.snapshot?.unreadable ?? [];

  const [tab, setTab] = useState<PersonalizationTabId>('agent');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const tablistRef = useRef<HTMLDivElement>(null);
  const [focusTab, setFocusTab] = useState(false);
  // Esc and Save unmount the textarea; focus returns to Edit, not <body>.
  const wasEditing = useRef(editing);

  const filesOf = (file: PersonalizationFileName): string =>
    api.snapshot?.files[file] ?? '';
  const valueOf = (file: PersonalizationFileName): string =>
    editing ? (drafts[file] ?? filesOf(file)) : filesOf(file);

  const selectedTab = useCallback(
    (): HTMLElement | null =>
      tablistRef.current?.querySelector<HTMLElement>(
        '[aria-selected="true"]',
      ) ?? null,
    [],
  );
  const activeTab =
    PERSONALIZATION_TABS.find((t) => t.id === tab) ?? PERSONALIZATION_TABS[0];
  // The first file the user can type into; an unreadable one has no textarea.
  const firstEditable = activeTab.files.find((f) => !unreadable.includes(f));
  useEffect(() => {
    // firstEditable is a dependency, not decoration: a snapshot refresh that
    // marks the edited file unreadable unmounts the textarea without changing
    // `tab`, and without re-running, focus would fall to <body> mid-edit.
    //
    // But re-running must only RECOVER focus, never move it. The same refresh
    // can also hand the textarea back, and refocusing unconditionally then
    // yanked the caret out of whatever the user was actually in — another
    // file, Save, a tab — on the very trigger this exists to handle. So act
    // only once focus has genuinely left the editor; a tab with no textarea
    // (every file unreadable) lands on the tab itself.
    if (editing) {
      if (!cardRef.current?.contains(document.activeElement)) {
        (textareaRef.current ?? selectedTab())?.focus();
      }
    } else if (wasEditing.current) {
      editButtonRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing, tab, selectedTab, firstEditable]);
  const owner = (): Element | null =>
    cardRef.current?.closest('[data-personalization-surface]') ??
    cardRef.current;

  const canSave = useCallback(
    (): boolean => editing && !saving,
    [editing, saving],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!canSave()) return;
    // Measure what will actually be WRITTEN, not what is in the textarea: the
    // save restores the stored file's line endings, and a draft that fits as
    // LF can cross the cap once CRLF goes back on. Checked for every dirty
    // file before any is written, so a refusal never leaves half the edit
    // saved.
    const tooBig = dirtyFiles.find(
      (file) =>
        personalizationByteLength(
          matchLineEndings(api.snapshot?.files[file] ?? '', drafts[file] ?? ''),
        ) > MAX_PERSONALIZATION_FILE_BYTES,
    );
    if (tooBig !== undefined) {
      setError(`${tooBig} is too large to save. Trim it and try again.`);
      return;
    }
    setError(null);
    try {
      await api.saveDrafts();
    } catch (err) {
      setError(
        err instanceof Error && err.message.length > 0
          ? err.message
          : 'Could not save your changes. Try again.',
      );
    }
  }, [api, canSave, dirtyFiles, drafts]);

  const discard = useCallback((): void => {
    api.discardEdit();
    setError(null);
  }, [api]);

  useSaveShortcut(
    editing,
    () => {
      if (!canSave()) return false;
      void save();
      return true;
    },
    owner,
  );
  useEscapeLayer(editing, discard, owner);

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const ids = PERSONALIZATION_TABS.map((t) => t.id);
    const i = ids.indexOf(tab);
    let next: PersonalizationTabId | undefined;
    if (e.key === 'ArrowRight') next = ids[(i + 1) % ids.length];
    else if (e.key === 'ArrowLeft')
      next = ids[(i - 1 + ids.length) % ids.length];
    else if (e.key === 'Home') next = ids[0];
    else if (e.key === 'End') next = ids[ids.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    setTab(next);
    setError(null);
    setFocusTab(true);
  };
  useEffect(() => {
    if (!focusTab) return;
    setFocusTab(false);
    selectedTab()?.focus();
  }, [focusTab, tab, selectedTab]);
  // A draft leaves the user's sight when its tab does, or when the editor
  // unmounts: write it to storage then rather than waiting for the interval.
  const { flushDrafts } = api;
  useEffect(() => () => flushDrafts(), [tab, flushDrafts]);

  const copy = async (): Promise<void> => {
    // An unreadable file reads as ""; copying that would be a silent truncation.
    const missing = activeTab.files.filter((f) => unreadable.includes(f));
    if (missing.length > 0) {
      setError(
        `${missing.join(', ')} couldn't be read on the agent, so there is nothing here to copy.`,
      );
      return;
    }
    if (!(await copyText(activeTab.files.map(valueOf).join('\n\n')))) {
      setError('Your browser did not allow the copy. Select the text instead.');
      return;
    }
    setError(null);
    setCopied(true);
  };
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(timer);
  }, [copied]);

  let statusLine: React.ReactNode = null;
  if (error != null) statusLine = <FieldError>{error}</FieldError>;
  else if (editing && dirtyFiles.length === 0) {
    statusLine = 'Editing your agent. Esc discards your changes.';
  } else if (editing) {
    const target = dirtyFiles.length === 1 ? 'it' : 'all of them';
    statusLine = `Unsaved: ${dirtyFiles.join(', ')}. Save writes ${target}; Esc discards.`;
  }

  return (
    <EditorCard ref={cardRef} $editing={editing}>
      <EditorTabBar>
        <EditorTabs
          ref={tablistRef}
          role="tablist"
          aria-label="Personalization sections"
          onKeyDown={onTabKeyDown}
        >
          {PERSONALIZATION_TABS.map((t) => (
            <EditorTab
              key={t.id}
              type="button"
              role="tab"
              id={`personalization-tab-${t.id}`}
              aria-controls={`personalization-panel-${t.id}`}
              aria-selected={tab === t.id}
              tabIndex={tab === t.id ? 0 : -1}
              $active={tab === t.id}
              onClick={() => {
                setTab(t.id);
                setError(null);
              }}
            >
              {t.label}
              {dirtyFiles.some((f) =>
                (t.files as readonly string[]).includes(f),
              ) && <DirtyDot aria-hidden="true" />}
            </EditorTab>
          ))}
        </EditorTabs>
        <EditorActions>
          <EditorActionButton
            type="button"
            disabled={!ready}
            onClick={() => void copy()}
            aria-label={`Copy ${activeTab.label}`}
          >
            <CopyIcon />
            {copied ? 'Copied' : 'Copy'}
          </EditorActionButton>
          {editing ? (
            <EditorSaveButton
              type="button"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save ⌘S'}
            </EditorSaveButton>
          ) : (
            <EditorActionButton
              ref={editButtonRef}
              type="button"
              disabled={!ready}
              onClick={() => {
                api.startEdit();
                setError(null);
              }}
              aria-label="Edit personalization"
            >
              <PencilIcon />
              Edit
            </EditorActionButton>
          )}
        </EditorActions>
      </EditorTabBar>
      <EditorBody
        role="tabpanel"
        id={`personalization-panel-${tab}`}
        aria-labelledby={`personalization-tab-${tab}`}
      >
        {activeTab.files.map((file) => (
          <FileSection
            key={file}
            file={file}
            value={valueOf(file)}
            editing={editing && !unreadable.includes(file)}
            unreadable={unreadable.includes(file)}
            inputRef={file === firstEditable ? textareaRef : undefined}
            onChange={(value) => api.setDraft(file, value)}
          />
        ))}
      </EditorBody>
      <EditorStatus role="status" aria-live="polite">
        {statusLine}
      </EditorStatus>
    </EditorCard>
  );
}

/**
 * One file: label, description, and the body — highlighted rows when reading,
 * a transparent textarea over the same rows when editing. Both layers share
 * the font metrics and padding constants below so they wrap identically.
 */
function FileSection({
  file,
  value,
  editing,
  unreadable,
  inputRef,
  onChange,
}: {
  readonly file: PersonalizationFileName;
  readonly value: string;
  readonly editing: boolean;
  readonly unreadable: boolean;
  readonly inputRef?: React.RefObject<HTMLTextAreaElement>;
  readonly onChange: (value: string) => void;
}) {
  const lines = useMemo(
    () => (editing ? editorLines(value) : markdownLines(value)),
    [editing, value],
  );
  return (
    <Section>
      <DescriptionLine>
        <Tok $color="heading">{PERSONALIZATION_FILE_LABELS[file]}</Tok>
        <Tok $color="dim">
          {' — '}
          {PERSONALIZATION_FILE_DESCRIPTIONS[file]}
        </Tok>
      </DescriptionLine>
      {unreadable && (
        <Notice role="status">
          {file} couldn&apos;t be read on the agent (too large, or not a regular
          file), so it is shown empty and read-only. Your file is untouched —
          nothing here can overwrite it. Fix it on the agent to edit it here
          again.
        </Notice>
      )}
      {editing ? (
        <EditStack>
          <EditHighlight aria-hidden="true">
            {lines.map((line, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <RenderedLine key={i} line={line} />
            ))}
          </EditHighlight>
          <EditorTextarea
            ref={inputRef}
            aria-label={`Edit ${file}`}
            value={value}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
          />
        </EditStack>
      ) : (
        <EditorPane aria-label={file}>
          {lines.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <RenderedLine key={i} line={line} />
          ))}
        </EditorPane>
      )}
    </Section>
  );
}

function RenderedLine({ line }: { readonly line: EditorLine }) {
  return (
    <CodeLine>
      <Gutter aria-hidden="true">{line.gutter}</Gutter>
      <LineText>
        {line.tokens.map((t, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Tok key={i} $color={t.color}>
            {t.text}
          </Tok>
        ))}
      </LineText>
    </CodeLine>
  );
}

// ── Icons ───────────────────────────────────────────────────────────────────

const IconSvg = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

function CopyIcon() {
  return (
    <IconSvg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="5.75"
        y="5.75"
        width="7.5"
        height="7.5"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M10.25 3.5v-.25A1.5 1.5 0 0 0 8.75 1.75h-4.5a1.5 1.5 0 0 0-1.5 1.5v4.5a1.5 1.5 0 0 0 1.5 1.5h.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </IconSvg>
  );
}

function PencilIcon() {
  return (
    <IconSvg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m2.75 13.25.5-3 7.5-7.5a1.06 1.06 0 0 1 1.5 0l1 1a1.06 1.06 0 0 1 0 1.5l-7.5 7.5-3 .5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </IconSvg>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const EditorCard = styled.div<{ $editing: boolean }>`
  margin: 4px 0 0;
  /* Read and edit render identically, so the card carries the mode. */
  border: 1px solid
    ${(p) => (p.$editing ? 'var(--accent-color)' : 'var(--border-color)')};
  border-radius: 8px;
  background: ${(p) =>
    p.$editing ? 'var(--bg-color-container)' : 'var(--bg-color-page)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const EditorTabBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px;
  border-bottom: 1px solid var(--divider-color);
  flex-shrink: 0;
`;

const EditorTabs = styled.div`
  display: flex;
  align-items: stretch;
  min-width: 0;
  overflow-x: auto;
  ${THIN_SCROLLBAR}
`;

const EditorTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 13px 12px 11px;
  border: none;
  border-bottom: 2px solid
    ${(p) => (p.$active ? 'var(--accent-color)' : 'transparent')};
  background: transparent;
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-label)')};
  font-family: ${MONO_FONT};
  font-size: 12.5px;
  letter-spacing: 0.01em;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -2px;
  }
`;

const DirtyDot = styled.span`
  display: inline-block;
  width: 5px;
  height: 5px;
  margin-left: 6px;
  vertical-align: middle;
  border-radius: 50%;
  background: var(--accent-color);
`;

const EditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const EditorActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary);
  font-family: inherit;
  ${TYPE.meta}
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--hover-bg);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const EditorSaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 4px;
  background: var(--accent-color);
  color: var(--cta-text-color);
  font-family: inherit;
  ${TYPE.meta}
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const EditorBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  ${THIN_SCROLLBAR}
`;

/* Shared by the read view and the edit overlay so the two cannot drift. */
const EDITOR_FONT_SIZE = '13.5px';
const EDITOR_GUTTER_WIDTH = '42px';
const EDITOR_PAD_RIGHT = '20px';
const EDITOR_PAD_TOP = '14px';
const EDITOR_PAD_BOTTOM = '18px';
const EDITOR_LINE_HEIGHT = 1.85;

/* No top padding, like EditHighlight: Section supplies EDITOR_PAD_TOP above
   the description in both modes, so toggling Edit does not shift line 1. */
const EditorPane = styled.div`
  padding: 0 0 ${EDITOR_PAD_BOTTOM};
  font-family: ${MONO_FONT};
  font-size: ${EDITOR_FONT_SIZE};
  line-height: ${EDITOR_LINE_HEIGHT};
`;

const CodeLine = styled.div`
  display: flex;
  align-items: flex-start;
`;

/* Sits outside the gutter's numbering, the way it sits outside the file. */
const DescriptionLine = styled.div`
  padding: 0 20px 0 14px;
  margin-bottom: ${EDITOR_LINE_HEIGHT / 2}em;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Gutter = styled.span`
  flex-shrink: 0;
  width: ${EDITOR_GUTTER_WIDTH};
  padding-right: 14px;
  text-align: right;
  color: var(--text-label);
  opacity: 0.7;
  user-select: none;
`;

const LineText = styled.span`
  flex: 1;
  min-height: ${EDITOR_LINE_HEIGHT}em;
  padding-right: ${EDITOR_PAD_RIGHT};
  white-space: pre-wrap;
  word-break: break-word;
`;

const TOKEN_COLORS: Readonly<Record<TokenColor, string>> = {
  plain: 'var(--text-primary)',
  dim: 'var(--text-label)',
  heading: 'var(--accent-color)',
  string: 'var(--warning-color)',
};

const Tok = styled.span<{ $color: TokenColor }>`
  color: ${(p) => TOKEN_COLORS[p.$color]};
`;

const Section = styled.div`
  &:not(:first-child) {
    margin-top: 26px;
  }
  display: flex;
  flex-direction: column;
  padding: ${EDITOR_PAD_TOP} 0 0;
  font-family: ${MONO_FONT};
  font-size: ${EDITOR_FONT_SIZE};
  line-height: ${EDITOR_LINE_HEIGHT};
`;

/* The highlight layer sizes the stack; the textarea is positioned over it, so
   EditorBody scrolls both together. */
const EditStack = styled.div`
  position: relative;
  flex: 1;
`;

const EditHighlight = styled.div`
  pointer-events: none;
  padding-bottom: ${EDITOR_PAD_BOTTOM};
`;

const EditorTextarea = styled.textarea`
  display: block;
  position: absolute;
  inset: 0;
  overflow: hidden;
  /* Only the caret and selection are drawn; the text is the layer beneath. */
  color: transparent;
  caret-color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0 ${EDITOR_PAD_RIGHT} ${EDITOR_PAD_BOTTOM} ${EDITOR_GUTTER_WIDTH};
  border: none;
  resize: none;
  background: transparent;
  font-family: ${MONO_FONT};
  font-size: ${EDITOR_FONT_SIZE};
  line-height: ${EDITOR_LINE_HEIGHT};

  &::selection {
    background: var(--accent-color);
    color: var(--cta-text-color);
  }

  &:focus-visible {
    outline: none;
  }
`;

const EditorStatus = styled.div`
  flex-shrink: 0;
  min-height: 24px;
  padding: 6px 20px 7px;
  border-top: 1px solid var(--divider-color);
  font-size: 12px;
  line-height: 18px;
  color: var(--text-label);
`;
